const BUILDER_MARKER = "CMS_BUILDER_JSON:";

function escapeHtml(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderInlineRichText(input) {
  const escaped = escapeHtml(input ?? "");
  const withLinks = escaped.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label, url) =>
      `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`
  );
  return withLinks.replace(/\n/g, "<br/>");
}

function styleToCss(style = {}) {
  const map = {
    margin: "margin",
    padding: "padding",
    color: "color",
    backgroundColor: "background-color",
    fontSize: "font-size",
    textAlign: "text-align",
    fontFamily: "font-family",
    fontWeight: "font-weight",
    fontStyle: "font-style",
    borderRadius: "border-radius",
    border: "border"
  };
  return Object.entries(map)
    .filter(([k]) => style[k] != null && String(style[k]).trim() !== "")
    .map(([k, cssKey]) => `${cssKey}:${String(style[k]).trim()}`)
    .join(";");
}

function blockToHtml(block) {
  const css = styleToCss(block.style);
  const attr = css ? ` style="${escapeHtml(css)}"` : "";
  const listItemsToHtml = (items) =>
    (Array.isArray(items) ? items : [])
      .map((x) => String(x ?? "").trim())
      .filter(Boolean)
      .map((x) => `<li>${renderInlineRichText(x)}</li>`)
      .join("");
  const nestedBlockToHtml = (nested) => {
    if (!nested || typeof nested !== "object") return "";
    if (nested.type === "heading") return `<h4>${renderInlineRichText(nested.content?.text || "Heading")}</h4>`;
    if (nested.type === "text") return `<p>${renderInlineRichText(nested.content?.text || "")}</p>`;
    if (nested.type === "image")
      return `<figure><img src="${escapeHtml(nested.content?.src || "")}" alt="${escapeHtml(nested.content?.alt || "")}" /></figure>`;
    if (nested.type === "list") {
      const lis = listItemsToHtml(nested.content?.items || []);
      return lis ? `<ul>${lis}</ul>` : "";
    }
    return "";
  };

  switch (block.type) {
    case "heading":
      return `<h2${attr}>${renderInlineRichText(block.content?.text || "Heading")}</h2>`;
    case "text":
      return `<p${attr}>${renderInlineRichText(block.content?.text || "")}</p>`;
    case "description": {
      const paragraphs = String(block.content?.text || "")
        .split(/\n{2,}/)
        .map((x) => x.trim())
        .filter(Boolean)
        .map((p) => `<p>${renderInlineRichText(p)}</p>`)
        .join("");
      return `<section${attr}>${paragraphs}</section>`;
    }
    case "image":
      return `<figure${attr}><img src="${escapeHtml(block.content?.src || "")}" alt="${escapeHtml(
        block.content?.alt || ""
      )}" /></figure>`;
    case "button":
      return `<p${attr}><a href="${escapeHtml(block.content?.href || "#")}" class="cms-btn">${escapeHtml(
        block.content?.label || "Button"
      )}</a></p>`;
    case "list": {
      const lis = listItemsToHtml(block.content?.items || []);
      return lis ? `<ul${attr}>${lis}</ul>` : "";
    }
    case "section": {
      const left = renderInlineRichText(block.content?.leftText || "");
      const right = renderInlineRichText(block.content?.rightText || "");
      return `<section class="cms-columns cms-columns-2"${attr}><div class="cms-col"><p>${left}</p></div><div class="cms-col"><p>${right}</p></div></section>`;
    }
    case "columns": {
      const cols = Number(block.content?.columns || 3);
      const items = Array.isArray(block.content?.items) ? block.content.items : [];
      const children = items
        .slice(0, Math.max(1, Math.min(4, cols)))
        .map((col, i) => {
          const blocks = Array.isArray(col?.blocks) ? col.blocks : [];
          const inner = blocks.map(nestedBlockToHtml).join("") || `<p>${renderInlineRichText(`Column ${i + 1} text`)}</p>`;
          return `<div class="cms-col">${inner}</div>`;
        })
        .join("");
      return `<section class="cms-columns cms-columns-${Math.max(1, Math.min(4, cols))}"${attr}>${children}</section>`;
    }
    case "box":
      return `<section class="cms-box"${attr}><h3>${renderInlineRichText(block.content?.title || "Box Title")}</h3><p>${renderInlineRichText(
        block.content?.text || ""
      )}</p></section>`;
    case "table": {
      const headers = Array.isArray(block.content?.headers) ? block.content.headers : [];
      const rows = Array.isArray(block.content?.rows) ? block.content.rows : [];
      const caption = String(block.content?.caption || "").trim();
      const head = headers
        .map((h) => `<th>${renderInlineRichText(h)}</th>`)
        .join("");
      const body = rows
        .map((row) => {
          const cells = Array.isArray(row) ? row : [];
          const tds = headers
            .map((_, i) => `<td>${renderInlineRichText(cells[i] || "")}</td>`)
            .join("");
          return `<tr>${tds}</tr>`;
        })
        .join("");
      const captionHtml = caption ? `<caption>${renderInlineRichText(caption)}</caption>` : "";
      return `<div class="cms-table-wrap"${attr}><table class="cms-table">${captionHtml}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
    }
    default:
      return "";
  }
}

export function blocksToHtml(blocks = []) {
  return blocks.map(blockToHtml).join("\n");
}

export function buildContentWithMetadata(blocks = []) {
  const json = JSON.stringify(blocks);
  return `<!--${BUILDER_MARKER}${json}-->\n${blocksToHtml(blocks)}`;
}

export function parseBlocksFromContent(content) {
  const source = String(content ?? "");
  const m = source.match(/<!--CMS_BUILDER_JSON:(.*?)-->/s);
  if (!m?.[1]) return null;
  try {
    const parsed = JSON.parse(m[1]);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function exportFullHtmlPage({ title, blocks }) {
  const body = blocksToHtml(blocks);
  const escapedTitle = escapeHtml(title || "CMS Page");
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapedTitle}</title>
  <style>
    body{font-family:Arial,sans-serif;line-height:1.6;margin:0;padding:24px;color:#0f172a}
    .cms-btn{display:inline-block;background:#0f172a;color:#fff;text-decoration:none;padding:10px 14px;border-radius:8px}
    .cms-columns{display:grid;gap:12px;margin:14px 0}
    .cms-columns-2{grid-template-columns:1fr 1fr}
    .cms-col{border:1px solid #e2e8f0;border-radius:10px;padding:10px}
    .cms-table-wrap{overflow-x:auto;margin:14px 0}
    .cms-table{width:100%;border-collapse:collapse;font-size:14px}
    .cms-table caption{caption-side:top;text-align:left;font-weight:700;margin-bottom:8px}
    .cms-table th,.cms-table td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
    .cms-table th{background:#f8fafc}
    img{max-width:100%;height:auto}
  </style>
</head>
<body>
${body}
</body>
</html>`;
}

